/**
 * Store Rebuild Orchestrator - Manages Store rebuilds from CRDT state
 *
 * This module handles the scheduling and execution of Store rebuilds
 * when CRDT state changes, ensuring Store is always a projection of CRDT.
 *
 * Extracted from crdt-bridge.js for better separation of concerns.
 * Uses a callback injection pattern to avoid circular dependencies.
 */

import { createLogger } from "../core/logger.js";
import { emit, Events } from "../core/events.js";
import { t } from "../../shared/i18n/index.js";
import { recordRebuildMetric } from "./crdt-metrics.js";
import {
  getActiveShareId,
  setActiveShareId,
  removeShareRole,
} from "./share-state.js";
import { hasSharedDoc, _handleSharedDocUpdateCore } from "./share-lifecycle.js";
import {
  mergeLocalDimensionsIntoTree,
  pruneLocalDimensions,
  getLocalDimensionsSize,
} from "./dimension-manager.js";
import { isLocalTxOrigin, ORPHAN_REPAIR_ORIGIN, USER_ORIGIN } from "./origins.js";

/**
 * If a single USER_ORIGIN transaction touches more than this fraction of
 * the total node set, log loudly. User actions are expected to be small
 * (drag, rename, create, delete). A huge USER_ORIGIN transaction most
 * likely means something is calling fromTree()/replaceTree() with the
 * wrong origin tag — which is exactly the failure mode we blame for the
 * destructive delta #280 investigation. We don't want such an event to
 * ever slip by silently again.
 */
const LARGE_TX_FRACTION_THRESHOLD = 0.5;

const log = createLogger("Store Rebuild");
const logError = (...args) => log.error(...args);
const logWarn = (...args) => log.warn(...args);
const logInfo = (...args) => log.info(...args);
const logDebug = (...args) => log.debug(...args);

// ============================================
// Module State
// ============================================

// Store rebuild debouncing state
let storeRebuildScheduled = false;
let storeRebuildRAFId = null;
let storeRebuildOrigin = null; // Track origin for logging ('local' | 'remote' | 'unknown')
let storeRebuildShareId = null; // Track target share for rebuild

// Corrupted document detection
let consecutiveToTreeFailures = 0;
let corruptedDocId = null; // Track which doc is corrupted to prevent repeated events
const MAX_TOTREE_FAILURES = 3; // After this many consecutive failures, emit DOCUMENT_CORRUPTED

// Main document observer cleanup function
let mainDocObserverCleanup = null;

// Track docNotes-only updates (don't trigger Store rebuild)
const DOC_NOTES_ARRAY_NAME = "docNotes";
const EXECUTION_MAP_NAME = "execution";

// ============================================
// Callback Injection
// ============================================

/**
 * Callbacks injected from crdt-bridge.js to access its internals
 */
let callbacks = {
  /** Get the main doc manager */
  getDocManager: null,
  /** Get current syncing depth */
  getSyncingDepth: null,
  /** Check if document is currently loading */
  isDocumentLoading: null,
  /** Get store module reference */
  getStore: null,
  /** Get VaultStorage module reference */
  getVaultStorage: null,
  /** Set last opened doc */
  setLastOpenedDoc: null,
  /** Check if pending remote update flag is set */
  getHasPendingRemoteUpdate: null,
  /** Set pending remote update flag */
  setHasPendingRemoteUpdate: null,
  /** Get docNotes update depth */
  getDocNotesUpdateDepth: null,
  /** Get linked doc tree by docId */
  getLinkedDocTree: null,
  /** Check if a linked doc is initialized */
  hasLinkedDoc: null,
  /** Check whether init failed because the user has no access */
  isLinkedDocAccessDenied: null,
  isLinkedDocGone: null,
  /** True while a linked map is unreachable and shown read-only from cache */
  isLinkedDocOffline: null,
  /** True if the user has already requested access for this docId */
  isAccessRequestPending: null,
  /** Rebuild linked node ownership map */
  rebuildLinkedNodeOwnership: null,
  /** Fire-and-forget init for any linkedDocIds not yet synced */
  ensureLinkedDocsForTree: null,
};

/**
 * Initialize the store rebuild orchestrator with callbacks
 * Must be called before using other functions
 * @param {Object} cbs - Callback functions
 */
export function initStoreRebuild(cbs) {
  callbacks = { ...callbacks, ...cbs };
  logDebug("Store rebuild orchestrator initialized");
}

// ============================================
// Public API
// ============================================

/**
 * Rebuild Store.data from CRDT (main document)
 *
 * This is the core function of the CRDT-first architecture.
 * Called after ANY change to the Yjs document (local or remote).
 *
 * The function:
 * 1. Converts CRDT to tree via docManager.toTree()
 * 2. Updates Store via _setDataFromCRDT()
 * 3. Emits SYNC_REMOTE_UPDATE for remote changes
 *
 * @param {string} origin - Origin of the change ('local' | 'remote' | 'unknown')
 * @param {YjsDocManager} [targetDocManager] - Doc manager to use (defaults to main)
 * @param {string} [shareId] - Share ID if rebuilding from shared doc
 */
export function rebuildStoreFromCRDT(origin = "unknown", targetDocManager = null, shareId = null) {
  const isDocumentLoading = callbacks.isDocumentLoading ? callbacks.isDocumentLoading() : false;

  // Skip during document transitions
  if (isDocumentLoading) {
    logDebug("rebuildStoreFromCRDT: Document loading, deferring");
    if (callbacks.setHasPendingRemoteUpdate) {
      callbacks.setHasPendingRemoteUpdate(true);
    }
    return;
  }

  // F3.2: Handle shared doc updates first (doesn't need main docManager)
  // This must come before the dm null check since shared docs look up their own doc manager
  if (shareId) {
    logDebug(`rebuildStoreFromCRDT: Shared doc ${shareId}, origin: ${origin}`);
    if (origin === "local") {
      // Local changes need synchronous update for consistent event timing
      _handleSharedDocUpdateCore(shareId);
    }
    // Remote changes are handled by the YjsDocManager onUpdate callback with debouncing
    return;
  }

  // Main doc rebuild - requires docManager
  const docManager = callbacks.getDocManager ? callbacks.getDocManager() : null;
  const dm = targetDocManager || docManager;
  if (!dm) {
    logWarn("rebuildStoreFromCRDT: No doc manager available");
    return;
  }

  // Defensive check - clear stale activeShareId if doc manager doesn't exist
  const activeShareId = getActiveShareId();
  if (activeShareId && !hasSharedDoc(activeShareId)) {
    logWarn(`Clearing stale activeShareId: ${activeShareId} (no doc manager)`);
    setActiveShareId(null);
    // Fall through - proceed with main-doc rebuild after clearing stale state
  }

  // Skip main-doc rebuilds when viewing a valid shared doc
  // Re-read after potential clear above (mirrors scheduleStoreRebuild pattern)
  const currentActiveShareId = getActiveShareId();
  if (currentActiveShareId) {
    logDebug("rebuildStoreFromCRDT: Skipping main-doc rebuild while viewing shared doc");
    return;
  }

  const store = callbacks.getStore ? callbacks.getStore() : null;
  if (!store) {
    logWarn("rebuildStoreFromCRDT: No store available");
    return;
  }

  // Phase 11 Fix: Reset document context if currentDocId references stale share
  // This handles the case where activeShareId was cleared but Store still has stale docId
  const currentDocId = store.get('currentDocId');
  const VaultStorage = callbacks.getVaultStorage ? callbacks.getVaultStorage() : null;

  // Only reset when ALL conditions are met:
  // 1. No valid active share (!currentActiveShareId || share doesn't exist)
  // 2. currentDocId is set and differs from main doc
  // 3. currentDocId references a non-existent share
  const needsContextReset = (!currentActiveShareId || !hasSharedDoc(currentActiveShareId)) &&
                            currentDocId &&
                            currentDocId !== dm.docId &&
                            !hasSharedDoc(currentDocId);

  if (needsContextReset) {
    logWarn(`rebuildStoreFromCRDT: Resetting stale share context (was: ${currentDocId})`);

    // Phase 12 Fix: Also update VaultStorage and lastOpenedDoc
    if (VaultStorage && VaultStorage.getCurrentDocId() !== dm.docId) {
      VaultStorage.setCurrentDocId(dm.docId);
    }
    if (callbacks.setLastOpenedDoc) {
      callbacks.setLastOpenedDoc(dm.docId);
    }
  }

  // A brand-new document has no tree yet; preference updates during bootstrap
  // must not count as repeated corruption failures.
  if (dm.nodeCount() === 0 && !dm.metadata?.get("rootId")) return;

  // Convert Yjs doc to tree
  const tree = dm.toTree();
  if (!tree) {
    // Track consecutive failures to detect corrupted documents
    consecutiveToTreeFailures++;
    const currentDocId = dm.docId;

    logWarn(`rebuildStoreFromCRDT: toTree() returned null (failure #${consecutiveToTreeFailures})`);

    // After MAX_TOTREE_FAILURES, emit DOCUMENT_CORRUPTED event (once per doc)
    if (consecutiveToTreeFailures >= MAX_TOTREE_FAILURES && corruptedDocId !== currentDocId) {
      corruptedDocId = currentDocId;
      logError(`Document ${currentDocId} appears corrupted - toTree() failed ${consecutiveToTreeFailures} times`);

      // Get document name from VaultStorage for user-friendly error
      let docName = t("sync.corrupted.unknownName");
      if (VaultStorage) {
        const index = VaultStorage.getIndex();
        const docEntry = index?.docs?.find((d) => d.docId === currentDocId);
        if (docEntry) {
          docName = docEntry.title || docEntry.name || t("common.untitled");
        }
      }

      // Emit event for UI to handle
      emit(Events.DOCUMENT_CORRUPTED, {
        docId: currentDocId,
        docName,
        reason: t("sync.corrupted.reason"),
      });
    }
    return;
  }

  // Reset failure counter on success
  consecutiveToTreeFailures = 0;

  // Rebuild Store from CRDT
  const startTime = performance.now();

  // F4.3: Merge local dimension cache into tree before Store rebuild
  // This restores dimension fields that are stored locally for shared docs
  if (getLocalDimensionsSize() > 0) {
    mergeLocalDimensionsIntoTree(tree);
  }

  // Phase 2.5 (PLAN17): Prune localDimensions for deleted nodes to prevent memory leak
  if (getLocalDimensionsSize() > 0) {
    pruneLocalDimensions(tree);
  }

  // Kick off sync for any linkedDocId references not yet initialized.
  // Fire-and-forget: when init finishes, handleLinkedDocUpdate triggers
  // another rebuild which will find hasLinkedDoc === true and splice content in.
  if (callbacks.ensureLinkedDocsForTree) {
    callbacks.ensureLinkedDocsForTree(tree);
  }

  // Composite linked document content at reference nodes
  if (callbacks.hasLinkedDoc) {
    compositeLinkedDocs(tree, dm.docId);
  }

  const accepted = store._setDataFromCRDT(tree, {
    preserveFocus: true,
    preserveSelection: true,
    source: origin,
    // Phase 11 Fix: Reset document context when exiting stale share.
    // Use `dm` (the manager this rebuild ran against) — needsContextReset
    // is computed against dm.docId, and `docManager` can be null when a
    // targetDocManager was passed.
    ...(needsContextReset && {
      docId: dm.docId,
      isShared: false,
      shareRole: null,
    }),
  });

  // Rebuild linked node ownership (edit routing) only for a tree the Store
  // accepted: routing must describe the tree the views show. A rejected tree
  // (e.g. duplicate ids) would otherwise route edits by nodes nobody sees.
  if (accepted !== false && callbacks.rebuildLinkedNodeOwnership) {
    callbacks.rebuildLinkedNodeOwnership(tree);
  }

  const elapsed = performance.now() - startTime;

  // F5.6: Record performance metric
  recordRebuildMetric(elapsed);

  if (elapsed > 5) {
    logWarn(`Store rebuild took ${elapsed.toFixed(1)}ms`);
  } else {
    logDebug(`Store rebuild: ${elapsed.toFixed(1)}ms`);
  }

  // Emit event for remote changes (UI components may want to show sync indicator)
  if (origin === "remote") {
    emit(Events.SYNC_REMOTE_UPDATE, { source: "crdt-observer" });
  }
}

/**
 * Composite linked document trees into the main tree at reference nodes.
 * Walks the tree, finds nodes with linkedDocId, and replaces their children
 * with the linked document's root children.
 * @param {Object} tree - The main document tree (mutated in place)
 */
function compositeLinkedDocs(tree, mainDocId = null) {
  if (!tree) return;
  // Track docIds composited at top level in this pass, to enforce C4 dedup:
  // only the first stub encountered for a given docId expands; any further
  // stubs for that docId render as duplicates. See docs/architecture/sharing.md.
  // `seenIds` holds every node id already in the composite (the host tree,
  // then each spliced linked map): copied or restored maps keep their node
  // ids, and a second node with the same id would misroute edits and make
  // the Store reject the whole tree.
  const seenIds = new Set();
  _collectIds(tree, seenIds);
  _compositeNode(tree, /* insideLinkedDoc */ false, /* composited */ new Set(), { mainDocId, seenIds });
}

// A link's own children are never shown (the composite replaces them), so
// they are not in the view. Clients before 2026-10-06 copied linked content
// there (crdt-bridge.js ensureStoreConsistency); counting those copies turned
// the link into "Open separately".
function _collectIds(node, ids) {
  if (!node) return;
  ids.add(node.id);
  if (node.linkedDocId) return;
  for (const child of node.children || []) _collectIds(child, ids);
}

function _hasIdConflict(nodes, seenIds) {
  for (const node of nodes || []) {
    if (seenIds.has(node.id) || _hasIdConflict(node.children, seenIds)) return true;
  }
  return false;
}

// Fields on the stub that represent main-doc context (layout, position, id,
// the link marker itself) and must NOT be overridden by the linked root.
const STUB_PRESERVED_FIELDS = new Set([
  "id",
  "linkedDocId",
  "linkedDocTitle",
  "x",
  "y",
  "mmX",
  "mmY",
  "mmSide",
  "mmOffsetX",
  "mmOffsetY",
  "mmWidth",
  "mmHeight",
  "mmTextHash",
  "collapsed",
]);

/**
 * Composite linked-doc content into the tree. Only one level of expansion is
 * ever performed (design decision C1):
 *   - A stub encountered at the top level of the main doc expands fully:
 *     the linked doc's root replaces the stub's displayed fields and its
 *     children become the stub's children.
 *   - A stub encountered INSIDE an already-expanded linked doc's subtree
 *     does NOT expand. It renders as an opaque badge (`_nestedReference`);
 *     views wire it to open the target doc as main on double-click.
 *   - A second stub for a docId already composited in this pass is marked
 *     `_duplicate` and rendered as a badge only (C4 defense-in-depth in
 *     case the picker guard is bypassed).
 *
 * @param {Object} node - Tree node to process
 * @param {boolean} insideLinkedDoc - True once we've descended into a
 *   composited linked-doc subtree. Subsequent stubs stay opaque.
 * @param {Set<string>} composited - docIds already composited at the top
 *   level of this pass. Dedup check against this set.
 */
function _compositeNode(node, insideLinkedDoc, composited, ctx = { mainDocId: null, seenIds: new Set() }) {
  if (!node || !node.children) return;

  for (let i = 0; i < node.children.length; i++) {
    const child = node.children[i];

    if (child.linkedDocId) {
      // Resolve a 3-state status used by views to render the right badge:
      //   "loaded"    — content available, splicing OK.
      //   "no-access" — ensureLinkedDocSync failed (no DEK in vault).
      //                 UI offers the request-access flow.
      //   "gone"      — no account owns the map any more (deleted, or
      //                 moved to a device): nothing to request.
      //   "loading"   — sync still in progress.
      const liveTree = callbacks.hasLinkedDoc(child.linkedDocId)
        ? callbacks.getLinkedDocTree(child.linkedDocId)
        : null;
      const accessDenied = !liveTree && !!callbacks.isLinkedDocAccessDenied?.(child.linkedDocId);
      const gone = accessDenied && !!callbacks.isLinkedDocGone?.(child.linkedDocId);
      const requestPending = accessDenied && !gone
        && !!callbacks.isAccessRequestPending?.(child.linkedDocId);
      const linkedState = liveTree
        ? "loaded"
        : accessDenied
          ? (gone ? "gone" : requestPending ? "pending-access" : "no-access")
          : "loading";
      const unavailableName = () => t(gone ? "sync.linked.gone" : "sync.linked.noAccess",
        { title: child.linkedDocTitle || t("sync.linked.defaultTitle") });

      // Nested or duplicate: render as opaque badge, do not splice.
      if (insideLinkedDoc || composited.has(child.linkedDocId)) {
        child.name = liveTree?.name
          || (linkedState === "no-access" || linkedState === "gone"
              ? unavailableName()
              : (child.linkedDocTitle || t("sync.linked.defaultTitle")));
        child.children = [];
        child._isReferenceNode = true;
        // Nested links never start their own channel (C1), so "loading"
        // would spin forever: show them as plain links to open instead.
        child._linkedState = insideLinkedDoc && linkedState === "loading" ? "nested" : linkedState;
        child._linkedDocLoaded = !!liveTree;
        child._nestedReference = insideLinkedDoc;
        child._duplicate = !insideLinkedDoc && composited.has(child.linkedDocId);
        continue;
      }

      // A map linked into itself, or linked content whose node ids already
      // appear in the composite: show a badge instead of splicing.
      const selfLink = !!ctx.mainDocId && child.linkedDocId === ctx.mainDocId;
      if (selfLink || (liveTree && _hasIdConflict(liveTree.children, ctx.seenIds))) {
        child.name = liveTree?.name || child.linkedDocTitle || t("sync.linked.defaultTitle");
        child.children = [];
        child._isReferenceNode = true;
        child._linkedState = "conflict";
        child._linkedDocLoaded = false;
        child._idConflict = true;
        continue;
      }

      // Top-level stub: splice linked doc's root into this position.
      if (liveTree) {
        for (const key of Object.keys(liveTree)) {
          if (STUB_PRESERVED_FIELDS.has(key)) continue;
          child[key] = liveTree[key];
        }
        child.children = liveTree.children || [];
        child._isReferenceNode = true;
        child._linkedState = callbacks.isLinkedDocOffline?.(child.linkedDocId) ? "offline" : "loaded";
        child._linkedDocLoaded = true;
        child._linkedRootId = liveTree.id;
        composited.add(child.linkedDocId);
        for (const spliced of child.children) _collectIds(spliced, ctx.seenIds);
        _compositeNode(child, /* insideLinkedDoc */ true, composited, ctx);
        continue;
      }
      // No content. Either still loading or the user has no access.
      child.name = linkedState === "no-access" || linkedState === "gone"
        ? unavailableName()
        : (child.linkedDocTitle || child.name || t("sync.linked.defaultTitle"));
      child.children = [];
      child._isReferenceNode = true;
      child._linkedState = linkedState;
      child._linkedDocLoaded = false;
    }

    _compositeNode(child, insideLinkedDoc, composited, ctx);
  }
}

/**
 * Schedule a Store rebuild using requestAnimationFrame for debouncing
 *
 * Multiple rapid CRDT changes (e.g., batch updates, concurrent edits)
 * are coalesced into a single Store rebuild for performance.
 *
 * For remote changes, uses rAF debouncing.
 * For local changes from the CRDT observer, prefer immediate rebuild.
 *
 * Phase 2.2 Fix: Accepts optional shareId to skip rebuilds for inactive shares.
 * This prevents shared doc updates from triggering main doc rebuilds.
 *
 * @param {string} origin - Origin of the change ('local' | 'remote')
 * @param {string} [shareId=null] - Share ID triggering the rebuild (null = main doc)
 */
export function scheduleStoreRebuild(origin, shareId = null) {
  // Track origin - 'remote' takes precedence (we want to emit SYNC_REMOTE_UPDATE)
  if (origin === "remote" || storeRebuildOrigin === null) {
    storeRebuildOrigin = origin;
  }

  const store = callbacks.getStore ? callbacks.getStore() : null;

  // Phase 2.2 Fix: Skip if rebuild is for a doc we're not viewing
  // This prevents shared doc updates from triggering rebuilds when viewing main doc
  const activeDocId = store ? store.get('currentDocId') : null;
  let activeShareId = getActiveShareId();

  // Defensive check: Clear stale activeShareId if doc manager doesn't exist
  // (Mirrors pattern in rebuildStoreFromCRDT for consistency)
  if (activeShareId && !hasSharedDoc(activeShareId)) {
    const staleShareId = activeShareId; // Capture before clearing
    logWarn(`scheduleStoreRebuild: Clearing stale activeShareId: ${staleShareId}`);
    setActiveShareId(null);
    activeShareId = null; // Keep local in sync with global for checks below

    // Phase 12 Fix: Clean up orphaned role entry for stale share
    // Note: Brief window where getActiveShareRole() returns null but store.shareRole still has
    // old value. Acceptable as UI components use store.shareRole exclusively, not both.
    removeShareRole(staleShareId);

    // Phase 12 Fix: If this rebuild was scheduled for the stale share,
    // switch to main-doc rebuild
    if (shareId && shareId === staleShareId) {
      shareId = null;
    }
    // Fall through - proceed with rebuild scheduling
  } else if (!shareId && activeShareId) {
    // Only skip if the share is valid (has a doc manager)
    logDebug("Skipping main-doc rebuild while viewing shared doc");
    return;
  }

  if (shareId && activeDocId && shareId !== activeDocId && shareId !== activeShareId) {
    logDebug(`Skipping rebuild for inactive share ${shareId} (active: ${activeDocId || activeShareId})`);
    return;
  }

  // Already scheduled, will include this change
  if (storeRebuildScheduled) {
    return;
  }

  storeRebuildScheduled = true;
  storeRebuildShareId = shareId;

  // Use requestAnimationFrame to batch changes within a frame
  storeRebuildRAFId = requestAnimationFrame(() => {
    storeRebuildScheduled = false;
    storeRebuildRAFId = null;
    const originToUse = storeRebuildOrigin || "unknown";
    let shareIdToUse = storeRebuildShareId; // Phase 12: Changed to let for stale share override
    storeRebuildOrigin = null;
    storeRebuildShareId = null;

    // Phase 2: RE-CHECK at execution time (user may have switched views)
    let currentActiveShareId = getActiveShareId();

    // Defensive check: Clear stale activeShareId if doc manager doesn't exist
    // (Mirrors pattern in rebuildStoreFromCRDT for consistency)
    if (currentActiveShareId && !hasSharedDoc(currentActiveShareId)) {
      const staleShareId = currentActiveShareId; // Capture before clearing
      logWarn(`rAF: Clearing stale activeShareId: ${staleShareId}`);
      setActiveShareId(null);
      currentActiveShareId = null; // Keep local in sync for checks below

      // Phase 12 Fix: Clean up orphaned role entry for stale share
      // Note: Brief window where getActiveShareRole() returns null but store.shareRole still has
      // old value. Acceptable as UI components use store.shareRole exclusively, not both.
      removeShareRole(staleShareId);

      // Phase 12 Fix: If rebuild was for the stale share, switch to main-doc
      if (shareIdToUse && shareIdToUse === staleShareId) {
        shareIdToUse = null;
      }
      // Fall through to rebuild
    } else if (!shareIdToUse && currentActiveShareId) {
      logDebug("Skipping main-doc rebuild - now viewing shared doc");
      return;
    }

    if (shareIdToUse && shareIdToUse !== currentActiveShareId) {
      logDebug(`Skipping rebuild - share ${shareIdToUse} no longer active`);
      return;
    }

    rebuildStoreFromCRDT(originToUse, null, shareIdToUse);
  });
}

/**
 * Cancel any pending scheduled Store rebuild.
 * Used when doing a synchronous rebuild to prevent double rebuilds.
 */
export function cancelScheduledRebuild() {
  if (storeRebuildRAFId !== null) {
    cancelAnimationFrame(storeRebuildRAFId);
    storeRebuildRAFId = null;
  }
  storeRebuildScheduled = false;
  storeRebuildOrigin = null;
  storeRebuildShareId = null;
}

// Edited notes → the Store's nodes: one pending refresh per doc manager.
const notesRefreshTimers = new Map();
const NOTES_REFRESH_DELAY = 400;

function scheduleNotesRefresh(dm) {
  clearTimeout(notesRefreshTimers.get(dm));
  notesRefreshTimers.set(dm, setTimeout(() => refreshNotesInStore(dm), NOTES_REFRESH_DELAY));
}

/**
 * Copy the notes an editor changed into the Store's nodes, in place, without
 * rebuilding the tree. Nodes not in the Store's tree (another map, a linked
 * map's own IDs) are left to the next rebuild.
 */
function refreshNotesInStore(dm) {
  clearTimeout(notesRefreshTimers.get(dm));
  notesRefreshTimers.delete(dm);
  const store = callbacks.getStore ? callbacks.getStore() : null;
  const changed = dm?.refreshDirtyNotes?.();
  if (!store || !changed?.size) return;
  for (const [nodeId, markdown] of changed) {
    const node = store.find(nodeId);
    if (node) node.notes = markdown;
  }
}

/** Refresh now any notes waiting for a pause in typing (an editor closed). */
export function flushNotesRefresh() {
  for (const dm of [...notesRefreshTimers.keys()]) refreshNotesInStore(dm);
}

/**
 * Setup CRDT observer on a Yjs document
 *
 * This observer triggers Store rebuilds on ANY change to the CRDT,
 * ensuring Store is always a projection of CRDT state.
 *
 * CRDT-First Event Timing (F1.2):
 *   - Local changes: Synchronous rebuild (no rAF debounce)
 *   - Remote changes: rAF-debounced rebuild (batches rapid updates)
 *
 * This ensures that when CRDTBridge API functions emit events,
 * the Store has already been updated with the new state.
 *
 * @param {YjsDocManager} targetDocManager - The doc manager to observe
 * @param {string} [shareId] - Share ID if this is a shared doc
 * @returns {Function} Cleanup function to remove observer
 */
export function setupCRDTObserver(targetDocManager, shareId = null) {
  if (!targetDocManager || !targetDocManager.doc) {
    logWarn("setupCRDTObserver: Invalid doc manager");
    return () => {};
  }

  // H4 Fix (PLAN18): Set up dedicated observer for docNotes Y.Array
  // This detects remote docNotes changes and emits DOC_NOTES_CHANGED
  // The main doc 'update' observer can't distinguish docNotes-only changes
  const docNotesArray = targetDocManager.doc.getArray(DOC_NOTES_ARRAY_NAME);
  const docNotesHandler = (event, transaction) => {
    // Only handle remote changes (local changes already visible in UI)
    // transaction.local is true for local changes
    if (transaction.local) return;

    const syncingDepth = callbacks.getSyncingDepth ? callbacks.getSyncingDepth() : 0;
    const docNotesUpdateDepth = callbacks.getDocNotesUpdateDepth ? callbacks.getDocNotesUpdateDepth() : 0;

    // CX-M4 Fix (PLAN21): Queue update if syncing, emit immediately otherwise
    if (syncingDepth > 0 || docNotesUpdateDepth > 0) {
      // Can't emit now - mark as pending
      if (callbacks.setHasPendingRemoteUpdate) {
        // Note: Using pendingDocNotesUpdate in original, but this is handled differently
        // The crdt-bridge tracks pendingDocNotesUpdate separately
      }
      logDebug("Remote docNotes change queued (syncingDepth > 0)");
    } else {
      logDebug("Remote docNotes change detected, emitting event");
      emit(Events.DOC_NOTES_CHANGED, { origin: 'remote' });
    }
  };
  docNotesArray.observe(docNotesHandler);

  // Same treatment for the execution map (Today lists): remote changes emit
  // EXECUTION_CHANGED; they never touch the tree, so no Store rebuild.
  const executionMap = targetDocManager.doc.getMap(EXECUTION_MAP_NAME);
  const executionHandler = (event, transaction) => {
    if (transaction.local) return;
    const syncingDepth = callbacks.getSyncingDepth ? callbacks.getSyncingDepth() : 0;
    if (syncingDepth > 0) {
      logDebug("Remote execution change queued (syncingDepth > 0)");
      return;
    }
    emit(Events.EXECUTION_CHANGED, { origin: "remote" });
  };
  executionMap.observe(executionHandler);

  const routinesMap = targetDocManager.doc.getMap('routines');
  let routineRefreshQueued = false, observerAlive = true;
  const routinesHandler = (_event, transaction) => {
    if (routineRefreshQueued) return;
    routineRefreshQueued = true;
    queueMicrotask(() => {
      routineRefreshQueued = false;
      if (observerAlive) {
        emit(Events.ROUTINES_CHANGED, {origin: transaction.local ? 'local' : 'remote'});
        emit(Events.EXECUTION_CHANGED, {origin: transaction.local ? 'local' : 'remote'});
      }
    });
  };
  routinesMap.observe(routinesHandler);
  const handler = (update, origin, _doc, transaction) => {
    if (transaction?.changed.size === 1 && transaction.changed.has(routinesMap)) return;
    // Skip orphan repair updates — toTree() already re-attached them in memory,
    // so the Store is already correct. No need to rebuild.
    if (origin === ORPHAN_REPAIR_ORIGIN) return;

    // Diagnostic: log huge USER_ORIGIN transactions. Real user actions
    // touch a handful of nodes; a huge USER_ORIGIN tx means a mis-tagged
    // bulk write slipped through (see LARGE_TX_FRACTION_THRESHOLD above).
    if (origin === USER_ORIGIN) {
      const total = targetDocManager.nodeCount();
      // Y.Doc update bytes aren't a perfect proxy, but big ones (>50KB) on
      // USER_ORIGIN are always suspicious.
      if (update && update.length > 50_000) {
        logError(
          `suspicious USER_ORIGIN tx: ${update.length} bytes, ${total} nodes in doc` +
            (shareId ? ` (share ${shareId})` : "") +
            ". This is much larger than a typical user edit — check for mis-tagged bulk writes.",
        );
      }
    }

    // Classify local vs remote. See engine/sync/origins.js for the full taxonomy.
    // ySyncPlugin (y-prosemirror) is local but fires per-keystroke.
    const isYSyncLocal = origin && typeof origin === 'object' && origin.key === 'y-sync$';
    const isStructuralLocal = !isYSyncLocal && isLocalTxOrigin(origin);
    const isLocal = isStructuralLocal || isYSyncLocal;
    const changeOrigin = isLocal ? "local" : "remote";

    // Skip during bulk operations (loadFromServer, syncFullTree, etc.)
    // This prevents double Store rebuilds when these functions both update CRDT
    // and call store.initialize() directly
    const syncingDepth = callbacks.getSyncingDepth ? callbacks.getSyncingDepth() : 0;
    if (syncingDepth > 0) {
      return;
    }

    // Sending a task may also change its status. Only skip the projection
    // when the transaction leaves nodes and their tag definitions untouched.
    const executionUpdateDepth = callbacks.getExecutionUpdateDepth ? callbacks.getExecutionUpdateDepth() : 0;
    if (executionUpdateDepth > 0 && !targetDocManager.touchesNodes(transaction) &&
        !transaction?.changed.has(targetDocManager.metadata)) {
      emit(Events.EXECUTION_CHANGED, { origin: changeOrigin });
      return;
    }

    // CR6 Fix: Skip Store rebuild for docNotes-only changes
    // DocNotes don't affect tree structure, so no rebuild needed
    // Using depth counter instead of boolean to prevent race conditions
    const docNotesUpdateDepth = callbacks.getDocNotesUpdateDepth ? callbacks.getDocNotesUpdateDepth() : 0;
    if (docNotesUpdateDepth > 0) {
      // Emit specific event for docNotes UI instead
      emit(Events.DOC_NOTES_CHANGED, { origin: changeOrigin });
      return;
    }

    // ySyncPlugin only edits Y.XmlFragments (note/cell bodies). Fragments are
    // not part of the tree (toTree() reads `nodes` Y.Map only), so a rebuild
    // would walk the entire CRDT, allocate a new tree, and reset Store data
    // — all for nothing structural. With per-keystroke firing this saturated
    // the main thread enough to starve y-indexeddb writes, causing data
    // loss on refresh. The active editor renders via its own ySyncPlugin
    // binding; cross-view fragment refresh goes through DOC_NOTES_CHANGED.
    if (isYSyncLocal) {
      emit(Events.DOC_NOTES_CHANGED, { origin: changeOrigin });
      // The Store's copy of the edited notes follows once typing pauses,
      // without a rebuild: views drawn later (another view, a cell closed and
      // redrawn) and any code reading node.notes see the edit.
      scheduleNotesRefresh(targetDocManager);
      return;
    }

    // For the main document, trigger a Store rebuild
    // For shared docs, also rebuild (was deferred to "Phase 1.5", now implemented)
    if (isStructuralLocal) {
      // Structural local changes (user/import/migration/layout/consistency):
      // synchronous rebuild for consistent event timing. Cancel any pending
      // rAF rebuild to avoid double rebuilds.
      cancelScheduledRebuild();
      rebuildStoreFromCRDT(changeOrigin, shareId ? targetDocManager : null, shareId);
    } else {
      // Remote structural changes: rAF-debounced rebuild.
      scheduleStoreRebuild(changeOrigin, shareId || null);
    }
  };

  targetDocManager.doc.on("update", handler);

  logInfo(`CRDT observer set up for ${shareId ? `shared:${shareId}` : "main doc"}`);

  return () => {
    targetDocManager.doc.off("update", handler);
    // H4 Fix (PLAN18): Also clean up docNotes observer
    docNotesArray.unobserve(docNotesHandler);
    executionMap.unobserve(executionHandler);
    observerAlive = false;
    routinesMap.unobserve(routinesHandler);
  };
}

/**
 * Get the main doc observer cleanup function
 * @returns {Function|null}
 */
export function getMainDocObserverCleanup() {
  return mainDocObserverCleanup;
}

/**
 * Set the main doc observer cleanup function
 * @param {Function|null} cleanup
 */
export function setMainDocObserverCleanup(cleanup) {
  mainDocObserverCleanup = cleanup;
}

/**
 * Check if a Store rebuild is currently scheduled
 * @returns {boolean}
 */
export function isRebuildScheduled() {
  return storeRebuildScheduled;
}

/**
 * Reset corruption detection state.
 * Called when switching to a different document.
 */
export function resetCorruptionState() {
  consecutiveToTreeFailures = 0;
  corruptedDocId = null;
}

/**
 * Check if a document is marked as corrupted
 * @param {string} docId - Document ID to check
 * @returns {boolean}
 */
export function isDocumentCorrupted(docId) {
  return corruptedDocId === docId;
}
